import { useEffect, useState, useRef } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Button, Divider, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { format } from "date-fns";
import { useQuery } from "react-query";
import styled from "styled-components";

import { request } from "@/Api";

const CommentButtonContainer = styled.div`
    text-align: right;
    margin-top: 12px;
`;

export function CommentInputWithPersonTagging({
    commentData,
    onCommentInput,
    onFocus,
    onBlur,
    fontSize,
    numRows,
    borderless,
    placeholder,
    multiline,
}) {
    const textFieldRef = useRef();
    const autocompleteRef = useRef();
    const [comment, setComment] = useState(commentData || "");
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);

    useEffect(() => {
        setComment(commentData);
    }, [commentData]);

    useEffect(() => {
        autocompleteOpen && autocompleteRef.current
            ? autocompleteRef.current.focus()
            : null;
    }, [autocompleteOpen]);

    const getPersons = () => {
        return request
            .get("persons", {
                params: {
                    offset: 0,
                    limit: 10000,
                },
            })
            .then((response) => {
                return response;
            });
    };

    const res =
        useQuery({
            queryKey: ["persons"],
            queryFn: getPersons,
            refetchOnWindowFocus: false,
        }).data || {};
    const persons = res.data || [];

    const handleOnCommentChange = (e) => {
        const cursorPos = e.target.selectionStart;
        const cursorChar = e.target.value.charAt(cursorPos - 1);
        if (cursorChar == "@") {
            setAutocompleteOpen(true);
        }
        setComment(e.target.value);
        onCommentInput(e.target.value);
    };

    const handleAutocompleteChange = (event, id) => {
        textFieldRef.current.focus();
        const cursorPos = textFieldRef.current.selectionStart;
        const newComment = [
            comment.slice(0, cursorPos),
            persons.find((person) => person.id == id)?.email,
            " ",
            comment.slice(cursorPos),
        ].join("");
        setComment(newComment);
        onCommentInput(newComment);
        setAutocompleteOpen(false);
    };

    const handleFocus = (e) => {
        setAutocompleteOpen(false);
        if (onFocus) {
            onFocus(e);
        }
    };

    const handleBlur = (e) => {
        if (onBlur) {
            onBlur(e);
        }
    };

    return (
        <>
            {autocompleteOpen ? (
                <Autocomplete
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    autoHighlight
                    open={autocompleteOpen}
                    onClose={() => {
                        setAutocompleteOpen(false);
                    }}
                    fullWidth
                    disablePortal
                    options={persons.map((person) => person.id.toString())}
                    getOptionLabel={(id) =>
                        persons.find((person) => person.id == id)?.email || ""
                    }
                    renderInput={(params) => (
                        <TextField
                            inputRef={autocompleteRef}
                            {...params}
                            label="Producer"
                        />
                    )}
                    onChange={handleAutocompleteChange}
                    sx={{ zIndex: 1 }}
                />
            ) : null}

            <TextField
                onClick={(e) => {
                    e.stopPropagation();
                }}
                placeholder={placeholder || "Comment..."}
                onFocus={handleFocus}
                onBlur={handleBlur}
                inputRef={textFieldRef}
                value={comment}
                onChange={handleOnCommentChange}
                fullWidth
                multiline={multiline !== undefined ? multiline : true}
                rows={numRows || 2}
                InputProps={{
                    style: { fontSize: fontSize || 16 },
                }}
                sx={borderless ? { "& fieldset": { border: "none" } } : {}}
                size="small"
            />
        </>
    );
}

export default function Comments({
    comments,
    modelType,
    modelInstanceId,
    onCommentAction,
}) {
    const [comment, setComment] = useState("");
    const [localComments, setLocalComments] = useState(comments);
    const [isCommentPending, setIsCommentPending] = useState(false);

    useEffect(() => {
        setLocalComments(comments);
    }, [comments]);

    const handleOnComment = () => {
        setIsCommentPending(true);
        request
            .post("/comments", {
                description: comment,
                model_type: modelType,
                model_instance_id: modelInstanceId,
            })
            .then((response) => {
                setComment("");
                setLocalComments((prevComments) => [
                    ...prevComments,
                    response.data,
                ]);
            })
            .finally(() => {
                setIsCommentPending(false);
            });
    };

    const handleResolve = (id) => () => {
        request.delete("/comments/" + id).then(() => {
            setLocalComments(
                localComments.map((comment) => {
                    return comment.id == id
                        ? { ...comment, archived_at: true }
                        : comment;
                }),
            );
        });
    };

    const handleCommentInput = (value) => {
        setComment(value);
    };

    return (
        <div>
            <CommentInputWithPersonTagging
                commentData={comment}
                onCommentInput={handleCommentInput}
            />
            <CommentButtonContainer>
                {isCommentPending ? (
                    <CircularProgress />
                ) : (
                    <Button onClick={handleOnComment}>Comment</Button>
                )}
            </CommentButtonContainer>

            <div>
                {localComments.map((comment, index) => (
                    <Comment
                        key={index}
                        comment={comment}
                        handleResolve={handleResolve}
                        onCommentAction={onCommentAction}
                    />
                ))}
            </div>
        </div>
    );
}

export function Comment({
    comment,
    handleResolve,
    showProjectLink,
    onCommentAction,
}) {
    return (
        <Grid container key={comment.id} sx={{ marginTop: "24px;" }}>
            <Grid container gap={1} flexWrap="nowrap">
                <Grid item>
                    <IconButton>
                        <Avatar imgProps={{ referrerPolicy: "no-referrer" }} />
                    </IconButton>
                </Grid>

                <Grid item flex={1} minWidth={0}>
                    <Grid container gap={1}>
                        <Grid item>
                            <Typography fontWeight="bold">
                                {comment.person_name}
                            </Typography>
                        </Grid>
                        <Grid item sx={{ color: "grey" }}>
                            <Typography>
                                {format(
                                    new Date(comment.created_at),
                                    "y-MM-dd",
                                )}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Typography>{comment.description}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid
                item
                xs={12}
                md={12}
                textAlign={"right"}
                sx={{ padding: "16px 0" }}
            >
                <CommentMetaDataComponent
                    comment={comment}
                    onCommentAction={onCommentAction}
                />
                {showProjectLink ? (
                    <Button
                        href={`/${comment.model_type}s/${comment.model_instance_id}`}
                    >{`Go to ${comment.model_type}`}</Button>
                ) : (
                    <>
                        {comment.archived_at ? (
                            <Button color="secondary" disabled>
                                Resolved
                            </Button>
                        ) : (
                            <Button onClick={handleResolve(comment.id)}>
                                Resolve
                            </Button>
                        )}
                    </>
                )}
            </Grid>
            <Divider sx={{ width: "100%" }} />
        </Grid>
    );
}

export function CommentMetaDataComponent({ comment, onCommentAction }) {
    if (!onCommentAction) {
        return null;
    }
    const handleCustomAction = () => {
        onCommentAction(comment);
    };
    if (!comment.meta) {
        return;
    }
    return (
        <IconButton onClick={handleCustomAction}>
            <ArrowForwardRoundedIcon fontSize="small" />
        </IconButton>
    );
}
